/**
 * Lo que se puede teclear en un campo de cantidad o de plata, y qué número sale de lo tecleado: dos
 * funciones puras que el campo usa en ese orden, filtrar la tecla y luego interpretar el texto.
 * `null` es campo vacío y no cero — un IVA sin desglose se guarda como «no hay dato».
 */

/** Deja pasar dígitos, y un separador decimal solo si el campo admite decimales. */
export function filtrarNumero(texto: string, decimales = false): string {
  const limpio = texto.replace(decimales ? /[^\d.,]/g : /\D/g, '');
  if (!decimales) return limpio;
  // Un solo separador, siempre el punto: lo que llegue después se descarta.
  const partes = limpio.replace(/,/g, '.').split('.');
  return partes.length <= 1 ? partes[0]! : `${partes[0]}.${partes.slice(1).join('')}`;
}

/** El número que representa el texto: `null` si está vacío; recortado a `min`/`max`. */
export function interpretarNumero(
  texto: string,
  { min, max, decimales = false }: { min?: number; max?: number; decimales?: boolean } = {}
): number | null {
  const limpio = filtrarNumero(texto, decimales);
  if (limpio === '' || limpio === '.') return null;
  let n = Number(limpio);
  if (!Number.isFinite(n)) return null;
  if (!decimales) n = Math.round(n);
  if (min !== undefined && n < min) n = min;
  if (max !== undefined && n > max) n = max;
  return n;
}

/**
 * Agrupa los dígitos de tres en tres (`50000` → `50.000`), sobre la cadena y no sobre el número:
 * `interpretarNumero` recorta con `min`, y teclear `0` con `min={1}` se vería como `1` de golpe.
 */
export function separarMiles(digitos: string): string {
  const limpio = digitos.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  return limpio === '' ? '' : limpio.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** El texto con que se muestra un valor que viene de fuera: vacío cuando no hay dato. */
export function textoDeNumero(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '';
  return String(valor);
}
